import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../App';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import GoLiveChecklist from '../../components/admin/GoLiveChecklist';
import ManageAccountDialog from '../../components/admin/ManageAccountDialog';
import EmailDeliveryStatusWidget from '../../components/admin/EmailDeliveryStatusWidget';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, MoreVertical, UserPlus, ShieldAlert, Eye, FileText,
  ClipboardList, StickyNote, Bell, UserX, ChevronUp, ChevronDown,
  Activity, Clock, Filter, ArrowRight, Upload, FlaskConical, Stethoscope,
  Mail, KeyRound, UserCheck, Shield, MapPin, Landmark, Pencil, Settings2, Copy, Send, Power
} from 'lucide-react';

const API = '/api';

const LIFECYCLE_STAGES = {
  invite_sent: { label: 'Invite Sent', color: 'bg-adm-bg0/20 text-slate-300 border-slate-500/30' },
  onboarding_started: { label: 'Onboarding Started', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  onboarding_completed: { label: 'Onboarding Done', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
  decode_pending: { label: 'DECODE Pending', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  decode_delivered: { label: 'DECODE Delivered', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  active_under_care: { label: 'Active Under Care', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  inactive: { label: 'Inactive', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
};

const CARE_STATES = {
  stable: { label: 'Stable', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40', dot: 'bg-emerald-400' },
  watch: { label: 'Watch', color: 'bg-amber-500/20 text-amber-400 border-amber-500/40', dot: 'bg-amber-400' },
  at_risk: { label: 'At Risk', color: 'bg-red-500/20 text-red-400 border-red-500/40', dot: 'bg-red-400' },
};

const ROLE_COLORS = {
  admin: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
  subscriber: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
  counselor: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  health_coach: 'bg-lime-500/20 text-lime-300 border-lime-500/30',
  doctor: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  caregiver: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  flow_steward: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  terra_head: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  terra_investor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
};

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function priorityScore(u) {
  let score = 0;
  if (u.care_state === 'at_risk') score += 1000;
  if (u.care_state === 'watch') score += 500;
  if (u.lifecycle_stage === 'decode_pending') score += 300;
  if (u.lifecycle_stage === 'invite_sent' || u.lifecycle_stage === 'onboarding_completed') score += 200;
  if (!u.last_activity) score += 150;
  else {
    const days = Math.floor((Date.now() - new Date(u.last_activity)) / 86400000);
    if (days > 7) score += 100;
  }
  return score;
}

function getNextAction(u) {
  if (!u || u.role !== 'subscriber') return null;
  const stage = u.lifecycle_stage;
  if (stage === 'invite_sent' || stage === 'onboarding_started')
    return { label: 'Complete Onboarding', icon: UserPlus, href: `/admin/users`, color: 'text-adm-tertiary' };
  if (stage === 'onboarding_completed' && !u.has_lab_data)
    return { label: 'Upload Labs', icon: Upload, href: `/admin/lab-intelligence?subscriber=${u.id}`, color: 'text-cyan-400' };
  if (!u.has_lab_data)
    return { label: 'Upload Labs', icon: Upload, href: `/admin/lab-intelligence?subscriber=${u.id}`, color: 'text-cyan-400' };
  if (!u.has_decode)
    return { label: 'Create DECODE', icon: FlaskConical, href: `/admin/decode-wizard?subscriber=${u.id}`, color: 'text-amber-400' };
  if (u.has_decode && !u.has_protocol)
    return { label: 'Create Protocol', icon: ClipboardList, href: `/admin/protocol-wizard?subscriber=${u.id}`, color: 'text-purple-400' };
  return { label: 'Monitor / Follow-up', icon: Stethoscope, href: `/admin/care-dashboard/${u.id}`, color: 'text-emerald-400' };
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [sortField, setSortField] = useState('priority');
  const [sortDir, setSortDir] = useState('desc');
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [stateDialog, setStateDialog] = useState(null);
  const [newCareState, setNewCareState] = useState('stable');
  const [editStaffDialog, setEditStaffDialog] = useState(null);
  const [editStaffForm, setEditStaffForm] = useState({ name: '', phone: '' });
  const [editStaffSaving, setEditStaffSaving] = useState(false);
  const [manageAccountUser, setManageAccountUser] = useState(null);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'subscriber' });
  const [actionLoading, setActionLoading] = useState(false);
  const [careTeamMembers, setCareTeamMembers] = useState([]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [teamForm, setTeamForm] = useState({
    name: '', email: '', phone: '', role: 'counselor',
    scope_type: 'city', city_id: '', centre_id: '',
    specializations: '',
  });
  const [inviteResult, setInviteResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [centres, setCentres] = useState([]);
  const [orgMeta, setOrgMeta] = useState({ tenants: [], cities: [], centres: [], facilitators: [], subscribers: [] });
  const [cityFilter, setCityFilter] = useState('all');
  const [centreFilter, setCentreFilter] = useState('all');
  const [assignmentMap, setAssignmentMap] = useState({});
  // Edit Scope dialog
  const [scopeDialog, setScopeDialog] = useState(null); // user object or null
  const [scopeForm, setScopeForm] = useState({ type: 'global', city_id: '', centre_id: '' });
  // Assign Subscriber dialog
  const [assignDialog, setAssignDialog] = useState(null); // subscriber user object or null
  const [assignForm, setAssignForm] = useState({ centre_id: '', city_id: '', primary_facilitator_id: '', secondary_facilitator_id: '' });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API}/users`);
      setUsers(resp.data);
      const team = resp.data.filter(u => ['counselor', 'health_coach', 'flow_steward', 'terra_head', 'terra_investor'].includes(u.role));
      setCareTeamMembers(team);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    axios.get(`${API}/terra/centres`).then(r => setCentres(r.data?.centres || [])).catch(() => {});
    axios.get(`${API}/org/metadata`).then(r => setOrgMeta(r.data || { tenants: [], cities: [], centres: [], facilitators: [], subscribers: [] })).catch(() => {});
    axios.get(`${API}/org/assignments?status=active`).then(r => {
      const map = {};
      (r.data?.assignments || []).forEach(a => { map[a.subscriber_id] = a; });
      setAssignmentMap(map);
    }).catch(() => {});
  }, [fetchUsers]);

  // ── Scope Dialog Handlers ──
  const openScopeDialog = (u) => {
    const s = u.tenant_scope || {};
    setScopeForm({ type: s.type || 'global', city_id: s.city_id || '', centre_id: s.centre_id || '' });
    setScopeDialog(u);
  };

  const handleSaveScope = async () => {
    if (!scopeDialog) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/terra/admin/user/${scopeDialog.id}/scope`, scopeForm);
      toast.success('Scope updated');
      setScopeDialog(null);
      fetchUsers();
      // Refresh metadata
      axios.get(`${API}/org/metadata`).then(r => setOrgMeta(r.data || orgMeta)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update scope'); }
    setActionLoading(false);
  };

  // ── Assignment Dialog Handlers ──
  const openAssignDialog = (subscriber) => {
    const existing = assignmentMap[subscriber.id];
    setAssignForm({
      centre_id: existing?.centre_id || '',
      city_id: existing?.city_id || '',
      primary_facilitator_id: existing?.primary_facilitator_id || '',
      secondary_facilitator_id: existing?.secondary_facilitator_id || '',
    });
    setAssignDialog(subscriber);
  };

  const handleSaveAssignment = async () => {
    if (!assignDialog || !assignForm.centre_id) { toast.error('Centre is required'); return; }
    setActionLoading(true);
    try {
      await axios.post(`${API}/terra/assignments`, {
        subscriber_id: assignDialog.id,
        centre_id: assignForm.centre_id,
        primary_facilitator_id: assignForm.primary_facilitator_id || null,
        secondary_facilitator_id: assignForm.secondary_facilitator_id || null,
      });
      toast.success('Assignment saved');
      setAssignDialog(null);
      // Refresh assignments
      const r = await axios.get(`${API}/org/assignments?status=active`);
      const map = {};
      (r.data?.assignments || []).forEach(a => { map[a.subscriber_id] = a; });
      setAssignmentMap(map);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save assignment'); }
    setActionLoading(false);
  };

  const scopeFormCities = orgMeta.cities;
  const scopeFormCentres = orgMeta.centres.filter(c => !scopeForm.city_id || c.city_id === scopeForm.city_id);
  const assignFormCentres = orgMeta.centres.filter(c => !assignForm.city_id || c.city_id === assignForm.city_id);
  const assignFormFacilitators = orgMeta.facilitators || [];

  const filtered = users.filter(u => {
    if (search) {
      const s = search.toLowerCase();
      if (!u.email?.toLowerCase().includes(s) && !u.name?.toLowerCase().includes(s)) return false;
    }
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (stageFilter !== 'all' && u.lifecycle_stage !== stageFilter) return false;
    if (stateFilter !== 'all' && u.care_state !== stateFilter) return false;
    // City/centre filters
    if (cityFilter !== 'all' || centreFilter !== 'all') {
      const scope = u.tenant_scope || {};
      const assignment = assignmentMap[u.id];
      const userCityId = scope.city_id || assignment?.city_id;
      const userCentreId = scope.centre_id || assignment?.centre_id;
      if (cityFilter !== 'all') {
        // Check city match via scope or via assignment's centre
        const centreInCity = orgMeta.centres.filter(c => c.city_id === cityFilter).map(c => c.centre_id);
        if (userCityId !== cityFilter && !centreInCity.includes(userCentreId)) return false;
      }
      if (centreFilter !== 'all') {
        if (userCentreId !== centreFilter) return false;
      }
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortField === 'priority') return sortDir === 'desc' ? priorityScore(b) - priorityScore(a) : priorityScore(a) - priorityScore(b);
    if (sortField === 'name') return sortDir === 'asc' ? (a.name || '').localeCompare(b.name || '') : (b.name || '').localeCompare(a.name || '');
    if (sortField === 'last_activity') {
      const da = a.last_activity ? new Date(a.last_activity) : new Date(0);
      const db_ = b.last_activity ? new Date(b.last_activity) : new Date(0);
      return sortDir === 'desc' ? db_ - da : da - db_;
    }
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />;
  };

  const handleAction = async (action, userId) => {
    setActionLoading(true);
    try {
      if (action === 'disable') await axios.patch(`${API}/users/${userId}/disable`);
      else if (action === 'enable') await axios.patch(`${API}/users/${userId}/enable`);
      else if (action === 'reminder') await axios.post(`${API}/users/${userId}/send-reminder`);
      else if (action === 'resend-invite') await axios.post(`${API}/users/${userId}/resend-invite`);
      else if (action === 'send-reset') await axios.post(`${API}/users/${userId}/send-reset`);
      fetchUsers();
    } catch (err) {
      console.error('Action failed:', err);
    } finally { setActionLoading(false); }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim() || !noteDialog) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/users/${noteDialog.id}/care-notes`, { note: noteText, note_type: 'general' });
      setNoteDialog(null);
      setNoteText('');
    } catch (err) { console.error(err); }
    finally { setActionLoading(false); }
  };

  const handleSaveCareState = async () => {
    if (!stateDialog) return;
    setActionLoading(true);
    try {
      await axios.patch(`${API}/users/${stateDialog.id}/care-state`, { care_state: newCareState });
      fetchUsers();
      setStateDialog(null);
    } catch (err) { console.error(err); }
    finally { setActionLoading(false); }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/subscribers`, {
        email: inviteForm.email,
        name: inviteForm.name,
        role: inviteForm.role
      });
      setInviteDialog(false);
      setInviteForm({ email: '', name: '', role: 'subscriber' });
      fetchUsers();
    } catch (err) { console.error(err); }
    finally { setActionLoading(false); }
  };

  const handleAddTeamMember = async () => {
    if (!teamForm.email || !teamForm.name) {
      toast.error('Name and email are required');
      return;
    }

    if (teamForm.role === 'subscriber' || teamForm.role === 'caregiver') {
      toast.error('Use "New Subscriber" for subscriber/caregiver creation');
      return;
    }

    // Phase 4: Scope enforcement for all staff roles
    const SCOPE_RULES = {
      flow_steward: { required: true, allowedTypes: ['centre'], label: 'Flow Steward must have Centre scope' },
      terra_head: { required: true, allowedTypes: ['city'], label: 'Terra Head must have City scope' },
      terra_investor: { required: true, allowedTypes: ['city'], label: 'Terra Investor must have City scope' },
      counselor: { required: true, allowedTypes: ['city', 'centre'], label: 'Counselor must have City or Centre scope' },
      health_coach: { required: true, allowedTypes: ['city', 'centre'], label: 'Health Coach must have City or Centre scope' },
      doctor: { required: true, allowedTypes: ['city', 'centre'], label: 'Doctor must have City or Centre scope' },
      admin: { required: false, allowedTypes: ['global', 'city', 'centre'], label: '' },
    };

    const rule = SCOPE_RULES[teamForm.role];
    if (rule) {
      if (rule.required && (!teamForm.scope_type || teamForm.scope_type === 'global')) {
        toast.error(rule.label);
        return;
      }
      if (teamForm.scope_type && !rule.allowedTypes.includes(teamForm.scope_type)) {
        toast.error(`Scope type "${teamForm.scope_type}" not allowed for ${teamForm.role.replace('_', ' ')}. ${rule.label}`);
        return;
      }
      if (teamForm.scope_type !== 'global' && !teamForm.city_id) {
        toast.error('City is required');
        return;
      }
      if (teamForm.scope_type === 'centre' && !teamForm.centre_id) {
        toast.error('Centre is required for centre scope');
        return;
      }
    }

    setActionLoading(true);
    try {
      const payload = {
        name: teamForm.name,
        email: teamForm.email,
        phone: teamForm.phone || null,
        role: teamForm.role,
        specializations: teamForm.specializations ? teamForm.specializations.split(',').map(s => s.trim()).filter(Boolean) : null,
      };

      // Always send tenant_scope for staff roles
      if (teamForm.scope_type) {
        payload.tenant_scope = {
          type: teamForm.scope_type,
          city_id: teamForm.scope_type !== 'global' ? (teamForm.city_id || null) : null,
          centre_id: teamForm.scope_type === 'centre' ? (teamForm.centre_id || null) : null,
        };
      }

      const res = await axios.post(`${API}/auth/invite-staff`, payload);
      setInviteResult(res.data);
      toast.success(`Invite created for ${teamForm.name}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    }
    finally { setActionLoading(false); }
  };

  const handleResendInvite = async (userId) => {
    try {
      const res = await axios.post(`${API}/auth/resend-invite/${userId}`);
      toast.success('Invite resent');
      setInviteResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Resend failed'); }
  };

  const handleRevokeInvite = async (userId) => {
    try {
      await axios.post(`${API}/auth/revoke-invite/${userId}`);
      toast.success('Invite revoked');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Revoke failed'); }
  };

  const copyInviteLink = (link) => {
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success('Invite link copied');
    setTimeout(() => setLinkCopied(false), 3000);
  };

  // Stats
  const subscribers = users.filter(u => u.role === 'subscriber');
  const atRisk = subscribers.filter(u => u.care_state === 'at_risk').length;
  const watching = subscribers.filter(u => u.care_state === 'watch').length;
  const pendingDecode = subscribers.filter(u => u.lifecycle_stage === 'decode_pending').length;
  const pendingOnboard = subscribers.filter(u =>
    u.lifecycle_stage === 'invite_sent' || u.lifecycle_stage === 'onboarding_completed'
  ).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight" data-testid="users-title">Users & Roles</h1>
            <p className="text-sm text-white/40 mt-1">{subscribers.length} subscribers | Staff management & scope governance</p>
          </div>
          <div className="flex gap-2">
            {currentUser?.role === 'admin' && (
              <Button
                onClick={() => setShowAddTeamMember(true)}
                className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30"
                data-testid="add-team-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
              </Button>
            )}
          </div>
        </div>

        {/* Email Delivery Status (admin-only, compact) */}
        {currentUser?.role === 'admin' && (
          <EmailDeliveryStatusWidget variant="compact" />
        )}

        {/* Priority Alerts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="priority-cards">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              <span className="text-xs text-red-400/70 uppercase tracking-wider font-medium">At Risk</span>
            </div>
            <span className="text-2xl font-bold text-red-400" data-testid="at-risk-count">{atRisk}</span>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-amber-400/70 uppercase tracking-wider font-medium">Watching</span>
            </div>
            <span className="text-2xl font-bold text-amber-400" data-testid="watch-count">{watching}</span>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-purple-400/70 uppercase tracking-wider font-medium">DECODE Pending</span>
            </div>
            <span className="text-2xl font-bold text-purple-400" data-testid="decode-pending-count">{pendingDecode}</span>
          </div>
          <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3.5">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus className="w-4 h-4 text-sky-400" />
              <span className="text-xs text-sky-400/70 uppercase tracking-wider font-medium">Pending Onboard</span>
            </div>
            <span className="text-2xl font-bold text-sky-400" data-testid="pending-onboard-count">{pendingOnboard}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="filters-bar">
          <div className="relative flex-1 min-w-[200px] max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-adm-surface/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[140px] bg-adm-surface/5 border-white/10 text-white/70" data-testid="role-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-white/30" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="subscriber">Subscriber</SelectItem>
              <SelectItem value="counselor">Counselor</SelectItem>
              <SelectItem value="health_coach">Health Coach</SelectItem>
              <SelectItem value="doctor">Doctor</SelectItem>
              <SelectItem value="caregiver">Caregiver</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="flow_steward">Flow Steward</SelectItem>
              <SelectItem value="terra_head">Terra Head</SelectItem>
              <SelectItem value="terra_investor">Terra Investor</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[170px] bg-adm-surface/5 border-white/10 text-white/70" data-testid="stage-filter">
              <SelectValue placeholder="Lifecycle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {Object.entries(LIFECYCLE_STAGES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-[140px] bg-adm-surface/5 border-white/10 text-white/70" data-testid="state-filter">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              <SelectItem value="stable">Stable</SelectItem>
              <SelectItem value="watch">Watch</SelectItem>
              <SelectItem value="at_risk">At Risk</SelectItem>
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={v => { setCityFilter(v); setCentreFilter('all'); }}>
            <SelectTrigger className="w-[140px] bg-adm-surface/5 border-white/10 text-white/70" data-testid="city-filter">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {orgMeta.cities.map(c => <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={centreFilter} onValueChange={setCentreFilter}>
            <SelectTrigger className="w-[160px] bg-adm-surface/5 border-white/10 text-white/70" data-testid="centre-filter">
              <SelectValue placeholder="Centre" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Centres</SelectItem>
              {orgMeta.centres.filter(c => cityFilter === 'all' || c.city_id === cityFilter).map(c => <SelectItem key={c.centre_id} value={c.centre_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-adm-surface/[0.03] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="users-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60" onClick={() => toggleSort('name')}>
                    <span className="flex items-center gap-1">Name <SortIcon field="name" /></span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Stage</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">State</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Phase</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60" onClick={() => toggleSort('last_activity')}>
                    <span className="flex items-center gap-1">Last Active <SortIcon field="last_activity" /></span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60" onClick={() => toggleSort('priority')}>
                    <span className="flex items-center gap-1">Priority <SortIcon field="priority" /></span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Next Action</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="text-center py-12 text-white/30">Loading...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-12 text-white/30">No users found</td></tr>
                ) : sorted.map(u => {
                  const stage = LIFECYCLE_STAGES[u.lifecycle_stage] || LIFECYCLE_STAGES.onboarding_completed;
                  const state = CARE_STATES[u.care_state] || CARE_STATES.stable;
                  const roleCls = ROLE_COLORS[u.role] || ROLE_COLORS.subscriber;
                  const pScore = priorityScore(u);
                  const isSubscriber = u.role === 'subscriber';
                  return (
                    <tr key={u.id} className="border-b border-white/[0.04] hover:bg-adm-surface/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {isSubscriber && u.care_state === 'at_risk' && (
                            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
                          )}
                          <div>
                            <div className="text-sm font-medium text-white/90">{u.name || 'No name'}</div>
                            <div className="text-xs text-white/35">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-[10px] ${roleCls}`}>
                          {u.role?.replace('_', ' ')}
                        </Badge>
                        {(() => {
                          const scope = u.tenant_scope || {};
                          const assign = assignmentMap[u.id];
                          if (isSubscriber) {
                            const cName = assign?.centre_name || orgMeta.centres.find(c => c.centre_id === assign?.centre_id)?.name;
                            return cName ? <Badge className="text-[8px] bg-purple-500/10 text-purple-400 border-0 ml-1">{cName}</Badge> : <Badge className="text-[8px] bg-zinc-500/10 text-zinc-400 border-0 ml-1">Unassigned</Badge>;
                          }
                          if (scope.type === 'centre') {
                            const cName = orgMeta.centres.find(c => c.centre_id === scope.centre_id)?.name || scope.centre_id;
                            return <Badge className="text-[8px] bg-purple-500/10 text-purple-400 border-0 ml-1">{cName}</Badge>;
                          }
                          if (scope.type === 'city') {
                            const cityName = orgMeta.cities.find(c => c.city_id === scope.city_id)?.name || scope.city_id;
                            return <Badge className="text-[8px] bg-blue-500/10 text-blue-400 border-0 ml-1">{cityName}</Badge>;
                          }
                          if (u.role !== 'subscriber' && u.role !== 'caregiver') {
                            const isLegacy = !scope.type || (scope.type === 'global' && !scope.tenant_id && u.role !== 'admin');
                            return <Badge className={`text-[8px] border-0 ml-1 ${isLegacy ? 'bg-amber-500/10 text-amber-400' : 'bg-white/5 text-white/30'}`}>{isLegacy ? 'Global (Legacy)' : 'Global'}</Badge>;
                          }
                          return null;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        {isSubscriber ? (
                          <Badge variant="outline" className={`text-[10px] ${stage.color}`}>
                            {stage.label}
                          </Badge>
                        ) : <span className="text-white/20 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {isSubscriber ? (
                          <button
                            onClick={() => { setStateDialog(u); setNewCareState(u.care_state || 'stable'); }}
                            className="flex items-center gap-1.5 group cursor-pointer"
                            data-testid={`care-state-${u.id}`}
                          >
                            <div className={`w-2 h-2 rounded-full ${state.dot}`} />
                            <Badge variant="outline" className={`text-[10px] ${state.color} group-hover:ring-1 ring-white/20 transition-all`}>
                              {state.label}
                            </Badge>
                          </button>
                        ) : <span className="text-white/20 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-white/50">{u.current_phase || '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-white/25" />
                          <span className="text-xs text-white/50">{timeAgo(u.last_activity || u.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isSubscriber && pScore > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <Activity className="w-3.5 h-3.5 text-white/25" />
                            <div className="w-16 h-1.5 rounded-full bg-adm-surface/10 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${pScore >= 1000 ? 'bg-red-400' : pScore >= 500 ? 'bg-amber-400' : pScore >= 200 ? 'bg-sky-400' : 'bg-adm-surface/20'}`}
                                style={{ width: `${Math.min(100, (pScore / 1200) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : <span className="text-white/20 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const action = getNextAction(u);
                          if (!action) return <span className="text-white/20 text-xs">—</span>;
                          const Icon = action.icon;
                          return (
                            <a
                              href={action.href}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium ${action.color} hover:underline group`}
                              data-testid={`next-action-${u.id}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {action.label}
                              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white/40 hover:text-white/70" data-testid={`actions-${u.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52 max-h-[70vh] overflow-y-auto">
                            {isSubscriber && (
                              <>
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Care Actions</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => window.location.href = `/admin/care-dashboard/${u.id}`} data-testid={`action-dashboard-${u.id}`}>
                                  <Eye className="w-4 h-4 mr-2" /> Open Care Dashboard
                                </DropdownMenuItem>
                                {u.has_decode && (
                                  <DropdownMenuItem onClick={() => window.location.href = `/admin/decodes?subscriber=${u.id}`} data-testid={`action-decode-${u.id}`}>
                                    <FileText className="w-4 h-4 mr-2" /> View DECODE
                                  </DropdownMenuItem>
                                )}
                                {u.has_protocol && (
                                  <DropdownMenuItem onClick={() => window.location.href = `/admin/protocols?subscriber=${u.id}`} data-testid={`action-protocol-${u.id}`}>
                                    <ClipboardList className="w-4 h-4 mr-2" /> View Protocol
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => { setNoteDialog(u); setNoteText(''); }} data-testid={`action-note-${u.id}`}>
                                  <StickyNote className="w-4 h-4 mr-2" /> Add Care Note
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleAction('reminder', u.id)} data-testid={`action-reminder-${u.id}`}>
                                  <Bell className="w-4 h-4 mr-2" /> Send Reminder
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Assignment</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => openAssignDialog(u)} data-testid={`action-assign-${u.id}`}>
                                  <Landmark className="w-4 h-4 mr-2" /> {assignmentMap[u.id] ? 'Reassign Centre' : 'Assign to Centre'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Account Actions</DropdownMenuLabel>
                                {['invite_sent', 'onboarding_pending', 'onboarding_started'].includes(u.lifecycle_stage) && (
                                  <DropdownMenuItem onClick={() => handleAction('resend-invite', u.id)} data-testid={`action-resend-invite-${u.id}`}>
                                    <Mail className="w-4 h-4 mr-2" /> Resend Invite
                                  </DropdownMenuItem>
                                )}
                                {!['invite_sent', 'onboarding_pending', 'onboarding_started'].includes(u.lifecycle_stage) && (
                                  <DropdownMenuItem onClick={() => handleAction('send-reset', u.id)} data-testid={`action-reset-pw-${u.id}`}>
                                    <KeyRound className="w-4 h-4 mr-2" /> Send Password Reset
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {!isSubscriber && u.role !== 'caregiver' && (
                              <>
                                {currentUser?.role === 'admin' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditStaffDialog(u);
                                      setEditStaffForm({ name: u.name || '', phone: u.phone || '' });
                                    }}
                                    data-testid={`action-edit-staff-${u.id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                                  </DropdownMenuItem>
                                )}
                                {currentUser?.role === 'admin' && (
                                  <DropdownMenuItem
                                    onClick={() => setManageAccountUser(u)}
                                    data-testid={`action-manage-account-${u.id}`}
                                  >
                                    <Settings2 className="w-4 h-4 mr-2" /> Manage Account
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Scope</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => openScopeDialog(u)} data-testid={`action-scope-${u.id}`}>
                                  <Shield className="w-4 h-4 mr-2" /> Edit Scope
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            {u.status !== 'disabled' ? (
                              <DropdownMenuItem onClick={() => handleAction('disable', u.id)} className="text-red-400" data-testid={`action-disable-${u.id}`}>
                                <UserX className="w-4 h-4 mr-2" /> Disable User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleAction('enable', u.id)} className="text-emerald-400" data-testid={`action-enable-${u.id}`}>
                                <UserCheck className="w-4 h-4 mr-2" /> Enable User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Staff Dialog (admin-only Edit Profile) */}
        <Dialog open={!!editStaffDialog} onOpenChange={() => { if (!editStaffSaving) setEditStaffDialog(null); }}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white" data-testid="edit-staff-dialog">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Full Name <span className="text-rose-400">*</span></label>
                <Input
                  value={editStaffForm.name}
                  onChange={e => setEditStaffForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Full name"
                  className="bg-adm-surface/5 border-white/10 text-white"
                  data-testid="edit-staff-name-input"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Phone</label>
                <Input
                  value={editStaffForm.phone}
                  onChange={e => setEditStaffForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="Optional"
                  className="bg-adm-surface/5 border-white/10 text-white"
                  data-testid="edit-staff-phone-input"
                />
              </div>
              <div className="text-[11px] text-white/40 leading-relaxed pt-1">
                Email, role, scope, and status are managed by their dedicated controls. To change those, use Edit Scope, Disable/Enable, or the role-change flow.
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditStaffDialog(null)}
                disabled={editStaffSaving}
                className="border-white/10 text-white/60"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const name = (editStaffForm.name || '').trim();
                  if (!name) {
                    toast.error('Name cannot be blank.');
                    return;
                  }
                  setEditStaffSaving(true);
                  try {
                    const payload = { name };
                    const phone = (editStaffForm.phone || '').trim();
                    if (phone || (editStaffDialog?.phone || '') !== '') {
                      payload.phone = phone;
                    }
                    await axios.patch(`${API}/users/${editStaffDialog.id}/personal-details`, payload);
                    toast.success('Team member updated successfully');
                    setEditStaffDialog(null);
                    fetchUsers();
                  } catch (e) {
                    const detail = e?.response?.data?.detail;
                    const msg = (typeof detail === 'string' && detail)
                      || (typeof detail === 'object' && detail?.message)
                      || 'Unable to update team member. Please try again.';
                    toast.error(msg);
                  } finally {
                    setEditStaffSaving(false);
                  }
                }}
                disabled={editStaffSaving || !(editStaffForm.name || '').trim()}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="edit-staff-save-btn"
              >
                {editStaffSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Account Dialog */}
        {manageAccountUser && (
          <ManageAccountDialog
            user={manageAccountUser}
            onClose={() => { setManageAccountUser(null); fetchUsers(); }}
          />
        )}

        {/* Care Note Dialog */}
        <Dialog open={!!noteDialog} onOpenChange={() => setNoteDialog(null)}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Add Care Note for {noteDialog?.name || noteDialog?.email}</DialogTitle>
            </DialogHeader>
            <Textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter care note..."
              className="bg-adm-surface/5 border-white/10 text-white min-h-[120px]"
              data-testid="care-note-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteDialog(null)} className="border-white/10 text-white/60">Cancel</Button>
              <Button onClick={handleSaveNote} disabled={actionLoading || !noteText.trim()} className="bg-teal-600 hover:bg-teal-700" data-testid="save-note-btn">
                Save Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Care State Dialog */}
        <Dialog open={!!stateDialog} onOpenChange={() => setStateDialog(null)}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Update Care State — {stateDialog?.name || stateDialog?.email}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 py-4">
              {Object.entries(CARE_STATES).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => setNewCareState(key)}
                  className={`p-3 rounded-lg border text-center transition-all ${newCareState === key ? `${val.color} ring-2 ring-white/30` : 'border-white/10 text-white/40 hover:border-white/20'}`}
                  data-testid={`state-option-${key}`}
                >
                  <div className={`w-3 h-3 rounded-full mx-auto mb-2 ${val.dot}`} />
                  <span className="text-sm font-medium">{val.label}</span>
                </button>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStateDialog(null)} className="border-white/10 text-white/60">Cancel</Button>
              <Button onClick={handleSaveCareState} disabled={actionLoading} className="bg-teal-600 hover:bg-teal-700" data-testid="save-state-btn">
                Update State
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite Dialog */}
        <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Invite New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                placeholder="Full name"
                value={inviteForm.name}
                onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                className="bg-adm-surface/5 border-white/10 text-white"
                data-testid="invite-name-input"
              />
              <Input
                placeholder="Email"
                value={inviteForm.email}
                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                className="bg-adm-surface/5 border-white/10 text-white"
                data-testid="invite-email-input"
              />
              <Select value={inviteForm.role} onValueChange={v => setInviteForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="bg-adm-surface/5 border-white/10 text-white" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscriber">Subscriber</SelectItem>
                  <SelectItem value="counselor">Counselor</SelectItem>
                  <SelectItem value="health_coach">Health Coach</SelectItem>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="flow_steward">Flow Steward</SelectItem>
                  <SelectItem value="terra_head">Terra Head</SelectItem>
                  <SelectItem value="terra_investor">Terra Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
              <Button onClick={handleInvite} disabled={actionLoading || !inviteForm.email || !inviteForm.name} className="bg-teal-600 hover:bg-teal-700" data-testid="send-invite-btn">
                Send Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Go-Live Readiness Checklist */}
        <GoLiveChecklist
          open={showChecklist}
          onClose={() => setShowChecklist(false)}
          onProceed={() => {
            setShowChecklist(false);
            setInviteDialog(true);
          }}
        />

        {/* Add Team Member Dialog */}
        <Dialog open={showAddTeamMember} onOpenChange={(o) => { if (!o) { setShowAddTeamMember(false); setInviteResult(null); } }}>
          <DialogContent className="sm:max-w-lg bg-[#1a1f2e] border-white/10" data-testid="add-team-member-dialog">
            <DialogHeader>
              <DialogTitle className="text-white font-semibold">{inviteResult ? 'Invite Created' : 'Add Team Member'}</DialogTitle>
            </DialogHeader>

            {inviteResult ? (
              /* Invite success — show link */
              <div className="space-y-4 py-2">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-sm text-emerald-300 mb-1">Invite created for {inviteResult.name}</p>
                  <p className="text-xs text-emerald-300/60">Role: {inviteResult.role} &middot; Expires: {new Date(inviteResult.expires_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-1 block">Invite Link (copy and share)</label>
                  <div className="flex gap-2">
                    <Input value={inviteResult.invite_link} readOnly className="bg-white/5 border-white/10 text-white text-xs flex-1" data-testid="invite-link" />
                    <Button onClick={() => copyInviteLink(inviteResult.invite_link)} variant="outline" className="border-white/10 text-white/80 text-xs" data-testid="copy-invite-link">
                      {linkCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] text-white/30">The team member will use this link to set their own password and activate their account.</p>
                <DialogFooter>
                  <Button onClick={() => { setShowAddTeamMember(false); setInviteResult(null); setTeamForm({ name: '', email: '', phone: '', role: 'counselor', scope_type: 'global', city_id: '', centre_id: '', specializations: '' }); }} className="bg-white/10 text-white/80">Done</Button>
                </DialogFooter>
              </div>
            ) : (
              /* Creation form */
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Full Name *</label>
                    <Input value={teamForm.name} onChange={e => setTeamForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Full name" className="bg-white/5 border-white/10 text-white" data-testid="team-name" />
                  </div>
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Email *</label>
                    <Input type="email" value={teamForm.email} onChange={e => setTeamForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="email@example.com" className="bg-white/5 border-white/10 text-white" data-testid="team-email" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-1 block">Phone</label>
                  <Input value={teamForm.phone} onChange={e => setTeamForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+91..." className="bg-white/5 border-white/10 text-white" data-testid="team-phone" />
                </div>
                <div>
                  <label className="text-xs text-white/50 mb-1 block">Role *</label>
                  <Select value={teamForm.role} onValueChange={v => {
                    const defaults = { flow_steward: 'centre', terra_head: 'city', terra_investor: 'city', admin: 'global', counselor: 'city', health_coach: 'centre', doctor: 'city' };
                    setTeamForm(p => ({ ...p, role: v, scope_type: defaults[v] || 'city', city_id: '', centre_id: '' }));
                  }}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="team-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1f2e] border-white/10">
                      <SelectItem value="counselor">Counselor</SelectItem>
                      <SelectItem value="health_coach">Health Coach</SelectItem>
                      <SelectItem value="doctor">Doctor</SelectItem>
                      <SelectItem value="flow_steward">Flow Steward</SelectItem>
                      <SelectItem value="terra_head">Terra Head</SelectItem>
                      <SelectItem value="terra_investor">Terra Investor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {['flow_steward', 'terra_head', 'terra_investor', 'counselor', 'health_coach', 'doctor', 'admin'].includes(teamForm.role) && (
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-3">
                    <p className="text-[10px] uppercase tracking-wider text-white/40">
                      Tenant Scope {teamForm.role !== 'admin' ? '(Required)' : '(Optional — defaults to Global)'}
                    </p>
                    <Select value={teamForm.scope_type} onValueChange={v => setTeamForm(p => ({ ...p, scope_type: v, city_id: v === 'global' ? '' : p.city_id, centre_id: v !== 'centre' ? '' : p.centre_id }))}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="team-scope-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1f2e] border-white/10">
                        {teamForm.role === 'admin' && <SelectItem value="global">Global</SelectItem>}
                        {!['flow_steward'].includes(teamForm.role) && <SelectItem value="city">City</SelectItem>}
                        {!['terra_head', 'terra_investor'].includes(teamForm.role) && <SelectItem value="centre">Centre</SelectItem>}
                      </SelectContent>
                    </Select>
                    {teamForm.scope_type !== 'global' && teamForm.scope_type && (
                      <>
                        <Select value={teamForm.city_id} onValueChange={v => setTeamForm(p => ({ ...p, city_id: v, centre_id: '' }))}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="team-city">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1f2e] border-white/10">
                            {orgMeta.cities.map(c => <SelectItem key={c.city_id} value={c.city_id} className="text-xs">{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {teamForm.scope_type === 'centre' && (
                          <Select value={teamForm.centre_id} onValueChange={v => setTeamForm(p => ({ ...p, centre_id: v }))}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white h-8 text-xs" data-testid="team-centre">
                              <SelectValue placeholder="Select centre" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a1f2e] border-white/10">
                              {orgMeta.centres.filter(c => !teamForm.city_id || c.city_id === teamForm.city_id).map(c => <SelectItem key={c.centre_id} value={c.centre_id} className="text-xs">{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </>
                    )}
                  </div>
                )}

                {teamForm.role === 'flow_steward' && (
                  <div>
                    <label className="text-xs text-white/50 mb-1 block">Specializations</label>
                    <Input value={teamForm.specializations} onChange={e => setTeamForm(p => ({ ...p, specializations: e.target.value }))}
                      placeholder="movement, mindfulness, nutrition (comma-separated)" className="bg-white/5 border-white/10 text-white text-xs" data-testid="team-specializations" />
                  </div>
                )}

                <p className="text-[10px] text-white/30">An invite link will be generated. The team member will set their own password.</p>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddTeamMember(false)} className="border-white/10 text-white/60">Cancel</Button>
                  <Button onClick={handleAddTeamMember} disabled={actionLoading}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="submit-team-member">
                    {actionLoading ? 'Creating...' : 'Create & Generate Invite'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Scope Dialog */}
        <Dialog open={!!scopeDialog} onOpenChange={() => setScopeDialog(null)}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white sm:max-w-md" data-testid="edit-scope-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-adm-accent" /> Edit Scope — {scopeDialog?.name || scopeDialog?.email}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.03] text-xs text-white/50">
                <span>Current: <strong className="text-white/80 capitalize">{(scopeDialog?.tenant_scope?.type || 'global')}</strong></span>
                {scopeDialog?.tenant_scope?.city_id && <span>City: <strong className="text-white/80">{orgMeta.cities.find(c => c.city_id === scopeDialog.tenant_scope.city_id)?.name || scopeDialog.tenant_scope.city_id}</strong></span>}
                {scopeDialog?.tenant_scope?.centre_id && <span>Centre: <strong className="text-white/80">{orgMeta.centres.find(c => c.centre_id === scopeDialog.tenant_scope.centre_id)?.name || scopeDialog.tenant_scope.centre_id}</strong></span>}
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Scope Type</label>
                <Select value={scopeForm.type} onValueChange={v => setScopeForm(p => ({ ...p, type: v, city_id: v === 'global' ? '' : p.city_id, centre_id: v !== 'centre' ? '' : p.centre_id }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="scope-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1a1f2e] border-white/10">
                    <SelectItem value="global">Global (all cities & centres in tenant)</SelectItem>
                    <SelectItem value="city">City (all centres in a city)</SelectItem>
                    <SelectItem value="centre">Centre (single facility)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scopeForm.type !== 'global' && (
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">City {scopeForm.type === 'city' || scopeForm.type === 'centre' ? '*' : ''}</label>
                  <Select value={scopeForm.city_id} onValueChange={v => setScopeForm(p => ({ ...p, city_id: v, centre_id: '' }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="scope-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1f2e] border-white/10">
                      {scopeFormCities.map(c => <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {scopeForm.type === 'centre' && (
                <div>
                  <label className="text-xs text-white/50 mb-1.5 block">Centre *</label>
                  <Select value={scopeForm.centre_id} onValueChange={v => setScopeForm(p => ({ ...p, centre_id: v }))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="scope-centre-select"><SelectValue placeholder="Select centre" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1f2e] border-white/10">
                      {scopeFormCentres.length === 0 ? <SelectItem value="" disabled>No centres in selected city</SelectItem> : scopeFormCentres.map(c => <SelectItem key={c.centre_id} value={c.centre_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setScopeDialog(null)} className="border-white/10 text-white/60">Cancel</Button>
              <Button onClick={handleSaveScope} disabled={actionLoading || (scopeForm.type === 'city' && !scopeForm.city_id) || (scopeForm.type === 'centre' && (!scopeForm.city_id || !scopeForm.centre_id))}
                className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90" data-testid="save-scope-btn">
                {actionLoading ? 'Saving...' : 'Update Scope'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Subscriber Dialog */}
        <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
          <DialogContent className="bg-[#1a1d23] border-white/10 text-white sm:max-w-md" data-testid="assign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-purple-400" /> {assignmentMap[assignDialog?.id] ? 'Reassign' : 'Assign'} — {assignDialog?.name || assignDialog?.email}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {assignmentMap[assignDialog?.id] && (
                <div className="p-2 rounded-lg bg-white/[0.03] text-xs text-white/50">
                  Current: <strong className="text-white/80">{assignmentMap[assignDialog?.id]?.centre_name || 'Unknown centre'}</strong>
                  {assignmentMap[assignDialog?.id]?.city_name && <span> in {assignmentMap[assignDialog?.id]?.city_name}</span>}
                </div>
              )}
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">City</label>
                <Select value={assignForm.city_id || '__all__'} onValueChange={v => setAssignForm(p => ({ ...p, city_id: v === '__all__' ? '' : v, centre_id: '' }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="assign-city"><SelectValue placeholder="All cities" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1f2e] border-white/10">
                    <SelectItem value="__all__">All Cities</SelectItem>
                    {orgMeta.cities.map(c => <SelectItem key={c.city_id} value={c.city_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Centre *</label>
                <Select value={assignForm.centre_id} onValueChange={v => setAssignForm(p => ({ ...p, centre_id: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="assign-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1f2e] border-white/10">
                    {assignFormCentres.map(c => <SelectItem key={c.centre_id} value={c.centre_id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Primary Facilitator</label>
                <Select value={assignForm.primary_facilitator_id || '__none__'} onValueChange={v => setAssignForm(p => ({ ...p, primary_facilitator_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="assign-facilitator"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1f2e] border-white/10">
                    <SelectItem value="__none__">None</SelectItem>
                    {assignFormFacilitators.map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({f.role?.replace('_', ' ')})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1.5 block">Secondary Facilitator</label>
                <Select value={assignForm.secondary_facilitator_id || '__none__'} onValueChange={v => setAssignForm(p => ({ ...p, secondary_facilitator_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="assign-secondary"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-[#1a1f2e] border-white/10">
                    <SelectItem value="__none__">None</SelectItem>
                    {assignFormFacilitators.filter(f => f.id !== assignForm.primary_facilitator_id).map(f => <SelectItem key={f.id} value={f.id}>{f.name} ({f.role?.replace('_', ' ')})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialog(null)} className="border-white/10 text-white/60">Cancel</Button>
              <Button onClick={handleSaveAssignment} disabled={actionLoading || !assignForm.centre_id}
                className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-assign-btn">
                {actionLoading ? 'Saving...' : (assignmentMap[assignDialog?.id] ? 'Reassign' : 'Assign')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
